import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  COPIED_FIELDS,
  LOGISTICS_FIELDS,
  eventName,
  generateHours,
  itemFor,
  showGaps,
  type LogisticsField,
  type OpeningDay,
  type TravelDraft,
  type TravelItem,
  TRAVEL_READ_FAILURES,
} from '@wr/shared';
import { del, errorMessage, get, post, put, putFile } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Checkbox, Dialog, Field, ManyOfChips, Notice } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';
import {
  candidateLabel,
  fmtDateTime,
  fmtDay,
  waitForDraft,
  type TeamCandidate,
  type TravelDocument,
} from './data.js';
import { useTrip } from './TravelScreen.js';

/**
 * Show logistics (admins, brief section 7.3; prototype: openTripEdit): the
 * team going, Copy from last edition, opening hours generated from the
 * dates and edited per day, Hours confirmed by the organizer, the show's
 * details and its documents. Copied fields are marked "Suggested, check it".
 */
export function LogisticsWindow() {
  const { page, reload } = useTrip();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useSignedIn();
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });
  const [people, setPeople] = useState<TeamCandidate[] | null>(null);
  const [team, setTeam] = useState(() => page.team.map((m) => m.id));
  const [fields, setFields] = useState<Partial<Record<LogisticsField, string>>>(
    () => page.logistics.fields,
  );
  const [suggested, setSuggested] = useState<LogisticsField[]>(() => page.logistics.suggested);
  const [hours, setHours] = useState<OpeningDay[]>(() => page.logistics.hours);
  const [confirmed, setConfirmed] = useState(page.logistics.hours_confirmed);
  const [documents, setDocuments] = useState<TravelDocument[]>(page.documents);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [manualPdf, setManualPdf] = useState<File | null>(null);
  const [manualState, setManualState] = useState<'idle' | 'reading' | 'done' | 'failed'>('idle');
  const [manualFailure, setManualFailure] = useState<TravelDraft['failure']>(null);
  const name = eventName(page.event);

  useEffect(() => {
    let current = true;
    get<{ items: TeamCandidate[] }>('/travel/people').then(
      ({ items }) => current && setPeople(items),
      (err: unknown) => current && setError(errorMessage(err)),
    );
    return () => {
      current = false;
    };
  }, []);

  if (!can('travel.logistics.manage')) {
    return (
      <Dialog small title="Not allowed" onClose={close}>
        <p className="note">Only an administrator edits the show logistics.</p>
      </Dialog>
    );
  }

  const set = (key: LogisticsField, value: string) => {
    setFields((f) => ({ ...f, [key]: value }));
    // Changed by hand: checked.
    setSuggested((s) => s.filter((k) => k !== key));
  };
  const copy = () => {
    const prev = page.previous;
    if (!prev) return;
    const next = { ...fields };
    const marked = new Set(suggested);
    for (const key of COPIED_FIELDS) {
      const value = prev.fields[key];
      if (!next[key] && value) {
        next[key] = value;
        marked.add(key);
      }
    }
    setFields(next);
    setSuggested([...marked]);
    if (hours.length === 0) setHours(generateHours(page.event.starts_on, page.event.ends_on));
    toast(`Copied from ${prev.name}. Check what changed.`);
  };
  const setHour = (i: number, key: 'open' | 'close', value: string) =>
    setHours((h) => h.map((day, j) => (j === i ? { ...day, [key]: value } : day)));

  /** Read the exhibitor manual: Claude fills the empty fields, each marked to check. */
  async function readManual() {
    const url = manualUrl.trim();
    if (!manualPdf && !/^https:\/\/\S+$/i.test(url)) {
      setError('Choose the PDF, or paste the address of the exhibitor page (https://).');
      return;
    }
    setError(null);
    setManualState('reading');
    try {
      const draft = manualPdf
        ? await putFile<TravelDraft>(
            `/travel/events/${page.event.id}/manual?name=${encodeURIComponent(manualPdf.name)}`,
            manualPdf,
            'POST',
          )
        : await post<TravelDraft>(`/travel/events/${page.event.id}/manual`, { url });
      const read = await waitForDraft(draft.id);
      void del(`/travel/drafts/${draft.id}`).catch(() => undefined);
      if (read.status !== 'ready' || !read.result || !('hours' in read.result)) {
        setManualFailure(read.failure ?? null);
        setManualState('failed');
        return;
      }
      const result = read.result;
      const next = { ...fields };
      const marked = new Set(suggested);
      for (const [key, value] of Object.entries(result.fields) as [LogisticsField, string][]) {
        if (!next[key] && value) {
          next[key] = value;
          marked.add(key);
        }
      }
      setFields(next);
      setSuggested([...marked]);
      if (hours.length === 0) {
        setHours(
          result.hours.length
            ? result.hours
            : generateHours(page.event.starts_on, page.event.ends_on),
        );
        setConfirmed(false);
      }
      setManualState('done');
    } catch (err) {
      setManualState('failed');
      setError(errorMessage(err));
    }
  }

  async function attach(files: FileList | null) {
    for (const file of [...(files ?? [])]) {
      try {
        const doc = await putFile<TravelDocument>(
          `/travel/events/${page.event.id}/documents?name=${encodeURIComponent(file.name)}`,
          file,
          'POST',
        );
        setDocuments((d) => [...d, doc]);
      } catch (err) {
        setError(errorMessage(err));
      }
    }
  }

  async function removeDocument(id: string) {
    try {
      await del(`/travel/documents/${id}`);
      setDocuments((d) => d.filter((x) => x.id !== id));
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function save() {
    setBusy(true);
    try {
      await put(`/travel/events/${page.event.id}/logistics`, {
        fields,
        suggested,
        hours,
        hours_confirmed: confirmed,
        team,
      });
      toast('Logistics saved');
      reload();
      close();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const options = [
    ...(people ?? []),
    // Someone on the team who is not active any more stays listed.
    ...page.team.filter((m) => !people?.some((p) => p.id === m.id)),
  ].map((p) => ({ value: p.id, label: candidateLabel(p) }));

  return (
    <Dialog
      title={`Logistics: ${name}`}
      className="wide"
      onClose={() => {
        reload();
        close();
      }}
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => {
              reload();
              close();
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
            Save
          </button>
        </>
      }
    >
      <div className="tv-tools">
        <button
          type="button"
          className="btn sm"
          disabled={!page.previous}
          title={page.previous ? undefined : 'No previous edition of this show'}
          onClick={copy}
        >
          Copy from {page.previous?.name ?? 'last edition'}
        </button>
        <button type="button" className="btn sm" onClick={() => setManualOpen((o) => !o)}>
          Read the exhibitor manual
        </button>
      </div>
      {manualOpen && (
        <div className="formsec">
          <h3>Exhibitor manual</h3>
          <p className="note travel-intro">
            Drop the PDF or paste the show&rsquo;s exhibitor page. Claude fills the empty fields;
            each suggestion is marked so you check it.
          </p>
          <input
            type="url"
            aria-label="Exhibitor page"
            placeholder="https://... exhibitor information page"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
          />
          <label className="btn sm travel-attach">
            Choose the PDF
            <input
              type="file"
              hidden
              accept="application/pdf"
              onChange={(e) => setManualPdf(e.target.files?.[0] ?? null)}
            />
          </label>
          {manualPdf && <p className="note travel-intro">Ready: {manualPdf.name}</p>}
          <button
            type="button"
            className="btn sm primary travel-attach"
            disabled={manualState === 'reading'}
            onClick={() => void readManual()}
          >
            Read it
          </button>
          {manualState === 'reading' && (
            <div className="sstep run">
              <span className="si" />
              <span>Reading the manual</span>
            </div>
          )}
          {manualState === 'done' && (
            <p className="note ok-tx travel-intro">
              Fields filled and marked &ldquo;Suggested&rdquo;. Opening hours added, not confirmed.
            </p>
          )}
          {manualState === 'failed' && (
            <p className="note travel-intro">
              Claude could not read it.{' '}
              {manualFailure ? `${TRAVEL_READ_FAILURES[manualFailure]} ` : ''}Fill the fields by
              hand.
            </p>
          )}
        </div>
      )}
      {!people && !error && <div className="spinner" aria-label="Loading" />}
      {people && (
        <ManyOfChips label="Team going" options={options} value={team} onChange={setTeam} />
      )}
      <div className="formsec">
        <h3>Opening hours</h3>
        <div className="actions">
          <button
            type="button"
            className="btn sm"
            onClick={() => setHours(generateHours(page.event.starts_on, page.event.ends_on))}
          >
            Generate from the dates
          </button>
        </div>
        {hours.length ? (
          hours.map((h, i) => (
            <div key={h.date} className="tv-hr">
              <span>
                {fmtDay(h.date)}
                {h.label && (
                  <>
                    {' '}
                    <small>{h.label}</small>
                  </>
                )}
              </span>
              <input
                type="time"
                aria-label={`Opens on ${fmtDay(h.date)}`}
                value={h.open}
                onChange={(e) => setHour(i, 'open', e.target.value)}
              />
              <input
                type="time"
                aria-label={`Closes on ${fmtDay(h.date)}`}
                value={h.close}
                onChange={(e) => setHour(i, 'close', e.target.value)}
              />
              <button
                type="button"
                className="x"
                aria-label={`Remove ${fmtDay(h.date)}`}
                onClick={() => setHours((all) => all.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))
        ) : (
          <p className="note">No hours yet.</p>
        )}
        <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}>
          Hours confirmed by the organizer
        </Checkbox>
      </div>
      <div className="formsec">
        <h3>Show details</h3>
        {LOGISTICS_FIELDS.map(([key, label]) => (
          <Field
            key={key}
            label={label}
            hint={
              suggested.includes(key) ? (
                <span className="badge b-soon">Suggested, check it</span>
              ) : undefined
            }
            value={fields[key] ?? ''}
            onChange={(e) => set(key, e.target.value)}
          />
        ))}
      </div>
      <div className="formsec">
        <h3>Documents</h3>
        {documents.length ? (
          documents.map((d) => (
            <div key={d.id} className="trow">
              <span>
                <Icon name="pdf" /> {d.name}
              </span>
              <b>
                <button
                  type="button"
                  className="linkbtn"
                  aria-label={`Remove ${d.name}`}
                  onClick={() => void removeDocument(d.id)}
                >
                  Remove
                </button>
              </b>
            </div>
          ))
        ) : (
          <p className="note">Exhibitor manual, hall plan, badges.</p>
        )}
        <label className="btn sm travel-attach">
          Attach a file
          <input
            type="file"
            hidden
            multiple
            accept="application/pdf,image/*"
            onChange={(e) => {
              void attach(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}

/** Team view (admins; prototype: openTeamView): per person, outbound, return, hotel. */
export function TeamWindow() {
  const { page } = useTrip();
  const navigate = useNavigate();
  const { can } = useSignedIn();
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });
  const name = eventName(page.event);
  const first = (userId: string, ok: (i: TravelItem) => boolean) =>
    page.items
      .filter((i) => itemFor(i, userId) && ok(i))
      .sort((a, b) => (a.fields.depart ?? '').localeCompare(b.fields.depart ?? ''))[0];
  const cell = (text: string | null) =>
    text ? <td className="ok-tx">{text}</td> : <td className="due-over">Missing</td>;
  const flight = (i: TravelItem | undefined) =>
    i
      ? `${[i.fields.carrier, i.fields.number].filter(Boolean).join(' ')}, ${fmtDateTime(i.fields.depart)}`
      : null;
  const gaps = showGaps(page.logistics);

  if (!can('travel.team.view')) {
    return (
      <Dialog small title="Not allowed" onClose={close}>
        <p className="note">Only an administrator sees the team view.</p>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={`Team view: ${name}`}
      className="wide"
      onClose={close}
      footer={
        <>
          {can('travel.logistics.manage') && (
            <button
              type="button"
              className="btn"
              onClick={() => navigate(`/travel/${page.event.id}/logistics`, { replace: true })}
            >
              Edit show logistics
            </button>
          )}
          <button type="button" className="btn primary" onClick={close}>
            Done
          </button>
        </>
      }
    >
      <div className="travel-table">
        <table className="tv-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Outbound</th>
              <th>Return</th>
              <th>Hotel</th>
            </tr>
          </thead>
          <tbody>
            {page.team.map((m) => {
              const hotel = first(m.id, (i) => i.type === 'hotel');
              return (
                <tr key={m.id}>
                  <td>
                    <b>{m.name}</b>
                  </td>
                  {cell(flight(first(m.id, (i) => i.type === 'flight' && i.fields.dir !== 'back')))}
                  {cell(flight(first(m.id, (i) => i.type === 'flight' && i.fields.dir === 'back')))}
                  {cell(
                    hotel
                      ? `${hotel.fields.name}, ${fmtDay(hotel.fields.check_in)} to ${fmtDay(hotel.fields.check_out)}`
                      : null,
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {page.team.length === 0 && <p className="note">Nobody is on the team yet.</p>}
      <p className="note travel-note">Show logistics: {gaps.join(', ') || 'complete'}.</p>
    </Dialog>
  );
}
